import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import { Box, Card, CardContent, CardMedia, Typography } from '@mui/material'

import DoctorIcon from '@/assets/svg/medecin.svg?react'
import PdsSvgIcon from '@/assets/svg/pds.svg?react'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectEstablishmentById } from '@/redux/reducers/establishment.reducer'
import { isStaff } from '@/utils/type-guards'
import { useMemo } from 'react'

export function WelcomeCard() {
  const theme = useTheme()
  const authUser = useAppSelector(selectAuthUser)
  const establishment = useAppSelector((state) => {
    if (!authUser || _.isUndefined(authUser.establishment)) return undefined

    if (typeof authUser.establishment !== 'string') return authUser.establishment

    return selectEstablishmentById(state, authUser.establishment)
  })

  const fullName = useMemo(() => {
    if (!authUser) return ''

    const name = `${authUser.userInfo.name} ${authUser.userInfo.surname}`
    if (name.length > 25) {
      return `${authUser.userInfo.name.split(' ').map((n) => n[0]).join('')} ${authUser.userInfo.surname}`
    }
    return name
  }, [authUser])

  const fontSize = useMemo(() => {
    if (fullName.length > 25) {
      return { xs: '16px', sm: '18px', md: '20px' } // adjust as needed
    }
    if (fullName.length > 20) {
      return { xs: '20px', sm: '24px', md: '28px' }
    }
    return { xs: '30px', sm: '36px', md: '40px' } // default size
  }, [fullName])

  const { t } = useTranslation()
  if (!authUser) return null
  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)',
        height: '130px',
        overflow: 'inherit',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <CardContent
          sx={{
            [theme.breakpoints.up('md')]: {
              pl: '40px',
            },
          }}
        >
          <Typography
            component="div"
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: fontSize,
              fontFamily: 'Dongle',
              lineHeight: { xs: '24px', sm: '30px', md: '36px' },
              color: 'white',
            }}
          >
            {t('page.home.welcome', {
              context: isStaff(authUser) ? (authUser.status === 'MEDECIN' ? 'doctor' : 'pds') : undefined,
              name: fullName,
            })}
          </Typography>
          {!!establishment && (
            <Typography
              component="div"
              variant="subtitle1"
              sx={{ mt: '5px', fontWeight: 500, fontSize: '30px', fontFamily: 'Dongle', lineHeight: '30px', color: 'white' }}
            >
              {establishment.name}
            </Typography>
          )}
          {
            !!authUser?.establishment?.logo?.url && (
              <img alt="logo" src={authUser?.establishment?.logo?.url ?? ''} height="48px" />
            )
          }
        </CardContent>
      </Box>
      {isStaff(authUser) && (
        <CardMedia component={authUser.status === 'MEDECIN' ? DoctorIcon : PdsSvgIcon} sx={{ width: 'auto', height: '180px', mt: '10px', mr: '40px' }} />
      )}
    </Card>
  )
}

export default WelcomeCard
